import { flow, makeObservable, observable } from 'mobx';

import { CaseAdapter } from '@shared/CaseAdapter';
import BaseStore from './BaseStore';
import RecoveryPointsAPI from '../api/recovery_points_api';

class RecoveryPointsStore extends BaseStore {
  isPointsDataLoading = false;
  points = [];
  reservations = [];
  isReservationsLoading = false;
  total = 0;
  departmentFilters = [];
  isLocationsListLoading = false;
  locations = [];
  paymentData = null;
  errorText = '';
  errorStatus = null;
  pointValidationData = null;
  validationStatus = null;
  promoValidationStatus = null;
  price = null;
  newPrice = null;
  reservationsList = [];
  commissionsList = [];
  commissionsStats = null;
  commissionsTotal = 0;
  isInvoiceUploading = false;
  invoicesList = [];
  referralLink = '';

  constructor() {
    super();

    makeObservable(this, {
      isPointsDataLoading: observable,
      points: observable,
      total: observable,
      departmentFilters: observable,
      isLocationsListLoading: observable,
      locations: observable,
      paymentData: observable,
      errorText: observable,
      isReservationsLoading: observable,
      reservations: observable,
      errorStatus: observable,
      validationStatus: observable,
      promoValidationStatus: observable,
      price: observable,
      newPrice: observable,
      reservationsList: observable,
      isInvoiceUploading: observable,
      invoicesList: observable,
      referralLink: observable,
    });
  }

  getPointsList = flow(function* ({
    date,
    latitude,
    longitude,
    radius = 50,
    limit = '6',
    page = '1',
    sort = 'distance',
    weekdays = '',
    departmentIds = '',
    locationIds = '',
  } = {}) {
    this.isPointsDataLoading = true;

    try {
      const { data } = yield RecoveryPointsAPI.getPointsList(
        date,
        latitude,
        longitude,
        radius,
        limit,
        page,
        sort,
        weekdays,
        departmentIds,
        locationIds
      );
      this.points = CaseAdapter.toFrontFormat(data.items);
      this.departmentFilters = data.filters.departments;
      if (this.total !== data.total) {
        this.total = data.total;
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.isPointsDataLoading = false;
    }
  });

  getLocationsList = flow(function* ({ latitude, longitude, sort = 'date' }) {
    this.isLocationsListLoading = true;
    try {
      const { data } = yield RecoveryPointsAPI.getLocationsList(latitude, longitude, sort);
      this.locations = CaseAdapter.toFrontFormat(data.items);
    } catch (e) {
      console.error(e);
      return [];
    } finally {
      this.isLocationsListLoading = false;
    }
  });

  createPointReservation = flow(function* (stageId) {
    this.isPointsDataLoading = true;
    this.isLoading = true;
    try {
      const { data } = yield RecoveryPointsAPI.createPointReservation(stageId);

      this.paymentData = data.payment_data;
      this.price = data.amount_cent / 100;
    } catch (e) {
      this.errorText = e.response?.data?.errorMessages?.message || 'Une erreur est survenue';
      this.errorStatus = e.response?.data?.errorMessages?.status || 500;
      console.error(e);
    } finally {
      this.isPointsDataLoading = false;
    }
  });

  createPointReservationForStudent = flow(function* (stageId, studentId) {
    this.isPointsDataLoading = true;
    this.isLoading = true;
    try {
      const { data } = yield RecoveryPointsAPI.createPointReservationForStudent(stageId, studentId);

      this.paymentData = data.payment_data;
      this.price = data.amount_cent / 100;
    } catch (e) {
      this.errorText = e.response?.data?.errorMessages?.message || 'Une erreur est survenue';
      this.errorStatus = e.response?.data?.errorMessages?.status || 500;
      console.error(e);
    } finally {
      this.isPointsDataLoading = false;
    }
  });

  getStudentPointsList = flow(function* () {
    this.isReservationsLoading = true;
    try {
      const { data } = yield RecoveryPointsAPI.getReservations();
      this.reservations = CaseAdapter.toFrontFormat(data);
      this.total = data.total;
    } catch (e) {
      console.error(e);
    } finally {
      this.isReservationsLoading = false;
    }
  });

  validatePointReservation = flow(function* (reservationId) {
    try {
      const { data } = yield RecoveryPointsAPI.validatePointReservation(reservationId);
      this.validationStatus = data.status;
    } catch (e) {
      this.errorStatus = e.response?.data?.errorMessages?.status || 500;
      this.errorText = e.response?.data?.errorMessages?.message || 'Une erreur est survenue';
      console.error(e);
    }
  });

  validatePromoCode = flow(function* (reservationId, code) {
    try {
      const { data } = yield RecoveryPointsAPI.validatePromoCode(reservationId, code);
      this.paymentData = data.payment_data;
      this.newPrice = data.amount_cent / 100;
      this.promoValidationStatus = 200;
    } catch (e) {
      this.promoValidationStatus = e.response?.data?.errorMessages?.status || 500;
      this.errorText = e.response?.data?.errorMessages?.message || 'Une erreur est survenue';
      console.error(e);
    }
  });

  searchReservations = flow(function* (page, limit, query) {
    this.isReservationsLoading = true;
    try {
      const { data } = yield RecoveryPointsAPI.searchReservations(page, limit, query);
      this.reservationsList = CaseAdapter.toFrontFormat(data.items);
      this.total = data.total;
    } catch (e) {
      console.error(e);
    } finally {
      this.isReservationsLoading = false;
    }
  });

  getCommissionsList = flow(function* (page, limit) {
    this.isReservationsLoading = true;
    try {
      const { data } = yield RecoveryPointsAPI.getCommissionsList(page, limit);
      this.commissionsList = CaseAdapter.toFrontFormat(data.items);
      this.commissionsTotal = data.total;
      this.commissionsStats = CaseAdapter.toFrontFormat(data.statistics);
    } catch (e) {
      console.error(e);
    } finally {
      this.isReservationsLoading = false;
    }
  });

  createInvoice = flow(function* (file: File, number?: string) {
    this.isInvoiceUploading = true;
    try {
      const { data } = yield RecoveryPointsAPI.createInvoice(file, number);
      return CaseAdapter.toFrontFormat(data);
    } catch (e) {
      this.errorText = e.response?.data?.errorMessages?.message || 'Une erreur est survenue';
      this.errorStatus = e.response?.data?.errorMessages?.status || 500;
      console.error(e);
      throw e;
    } finally {
      this.isInvoiceUploading = false;
    }
  });

  getInvoicesList = flow(function* (page, limit) {
    this.isReservationsLoading = true;
    try {
      const { data } = yield RecoveryPointsAPI.getInvoicesList(page, limit);
      this.invoicesList = data;
      this.total = data.total;
    } catch (e) {
      console.error(e);
    } finally {
      this.isReservationsLoading = false;
    }
  });

  getReferralLink = flow(function* () {
    try {
      const { data } = yield RecoveryPointsAPI.getReferralLink();
      this.referralLink = data.referral_link;
    } catch (e) {
      console.error(e);
    }
  });
}

export default new RecoveryPointsStore();
